import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { ItalicFontStyle, RegularFontStyle, notEmpty, weightMap } from "@superwall-me/schema";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { SDropdownV3 } from "../../SDropdownV3";
import { SSegmentedControl } from "../../SSegmentedControl";
import { SVariablePicker } from "../../SVariablePicker";
import STooltip from "../../Tooltip";
import { SIcon } from "../../components";
import { useEditorAPI } from "../providers";
import { getLabelFromVariants, getStyleFromVariant, getVariantOptions } from "./helpers/fonts";
import { sortBy } from "es-toolkit/compat";
import { startCase } from "es-toolkit";
export const CSSFontEditor = (props)=>{
    const { value: _value, onChangeValue } = props;
    const { openEditFontMenu, fonts: _fontsAPI } = useEditorAPI();
    const { useDefaultFont, useFonts } = _fontsAPI;
    // Setup defaults
    const { fontFace: defaultFont, variant: _variant } = useDefaultFont();
    const defaultValue = {
        type: "css-font",
        value: defaultFont.family,
        style: RegularFontStyle,
        variant: _variant,
        kind: defaultFont.kind,
        url: defaultFont.files[_variant] || "",
        weight: _variant
    };
    const fontsMap = useFonts();
    const fonts = sortBy(Object.values(fontsMap).filter(notEmpty), (font)=>{
        if (font.kind === "system") {
            return "1" + font.family;
        }
        return "2" + font.family;
    });
    const pickerItems = useMemo(()=>{
        return fonts.map((font)=>{
            return {
                key: font.family,
                name: font.family,
                state: font
            };
        });
    }, [
        fonts
    ]);
    // Make sure we always have a value
    const value = typeof _value === "undefined" ? defaultValue : _value;
    const variant = useMemo(()=>value?.variant, [
        value
    ]);
    const onChange = useCallback((value)=>{
        onChangeValue && onChangeValue(value);
    }, [
        onChangeValue
    ]);
    // What possible variants do we have
    const selectedFontFamily = useMemo(()=>{
        let font = fonts[0];
        if (value && fonts.length > 0) {
            const found = fonts.find((font)=>font.family === value.value);
            if (found) {
                font = found;
            }
        }
        return font;
    }, [
        _value,
        fonts
    ]);
    const variantOptions = useMemo(()=>{
        if (selectedFontFamily?.variants) {
            return getVariantOptions(selectedFontFamily);
        }
        return [];
    }, [
        selectedFontFamily
    ]);
    console.log(`!!! variant options`, variantOptions, selectedFontFamily);
    // What is the selected variant
    const variantOption = useMemo(()=>{
        return value ? variantOptions.find((option)=>option.value === value.weight) : undefined;
    }, [
        value,
        variantOptions
    ]);
    // Handle menu width
    const ref = useRef(null);
    const [menuWidth, setMenuWidth] = useState(200);
    useEffect(()=>{
        if (ref.current) {
            setMenuWidth(ref.current.getBoundingClientRect().width);
        }
    }, [
        ref.current
    ]);
    return /*#__PURE__*/ _jsx(SVariablePicker, {
        menuWidth: menuWidth,
        items: pickerItems,
        asButton: true,
        onAdd: openEditFontMenu,
        addButtonRender: openEditFontMenu ? ()=>{
            return /*#__PURE__*/ _jsx(SIcon, {
                size: 13,
                type: "plus"
            });
        } : undefined,
        buttonRender: ({ isOpen, setIsOpen })=>{
            return /*#__PURE__*/ _jsxs("div", {
                ref: ref,
                className: `grid gap-2 ${value && variantOption && variantOption?.styles.length > 1 ? "grid-cols-[1fr,auto]" : "grid-cols-1"}`,
                children: [
                    /*#__PURE__*/ _jsxs("div", {
                        className: `w-full grid ${value ? "grid-cols-[1fr,auto]" : "grid-cols-1"} gap-3 bg-grayscale-dark rounded-md items-center border border-white/10 h-[32px] px-2 text-[13px]`,
                        children: [
                            /*#__PURE__*/ _jsxs("div", {
                                onClick: ()=>setIsOpen(true),
                                className: "h-full grid grid-cols-[auto,1fr] gap-2 cursor-pointer items-center overflow-hidden",
                                children: [
                                    /*#__PURE__*/ _jsx(SIcon, {
                                        className: "text-[#6B7280] shrink-0",
                                        type: "search",
                                        size: 12
                                    }),
                                    /*#__PURE__*/ _jsx("div", {
                                        className: "overflow-hidden",
                                        children: _value?.value ? /*#__PURE__*/ _jsx("p", {
                                            className: "truncate",
                                            children: _value?.value
                                        }) : /*#__PURE__*/ _jsx("p", {
                                            className: "text-[#6B7280] truncate",
                                            children: defaultFont.family
                                        })
                                    })
                                ]
                            }),
                            value && /*#__PURE__*/ _jsx(SDropdownV3, {
                                borderless: true,
                                showIcon: false,
                                className: "border-l h-full pl-2 flex items-center justify-center border-white/10 shrink-0",
                                placeholder: "Normal",
                                options: variantOptions,
                                value: _value ? {
                                    label: startCase(weightMap[value.weight] || value.weight),
                                    value: value.weight
                                } : {
                                    label: "Normal",
                                    value: RegularFontStyle
                                },
                                onChange: (option)=>{
                                    const v = variant?.includes(ItalicFontStyle) ? option.styles.find((s)=>s.value.includes(ItalicFontStyle))?.value : option.styles[0].value;
                                    onChange({
                                        type: "css-font",
                                        value: selectedFontFamily?.family || "",
                                        style: v && v.includes(ItalicFontStyle) ? ItalicFontStyle : RegularFontStyle,
                                        variant: v,
                                        kind: selectedFontFamily?.kind,
                                        url: selectedFontFamily?.files[option.styles[0].value] || "",
                                        weight: option.value
                                    });
                                },
                                children: /*#__PURE__*/ _jsx("div", {
                                    style: {
                                        fontWeight: value.weight
                                    },
                                    children: startCase(weightMap[value.weight] || value.weight)
                                })
                            })
                        ]
                    }),
                    value && variantOption && variantOption?.styles.length > 1 && /*#__PURE__*/ _jsx(SSegmentedControl, {
                        iconSize: 12,
                        options: variantOption?.styles || [],
                        value: _value ? (variantOption?.styles || []).find((style)=>style.value === value.variant) : undefined,
                        onChange: (option)=>{
                            const foundStyle = variantOption?.styles.find((style)=>style.value === option.value);
                            console.log(`!!! Segmented Control`, option, value?.url, foundStyle);
                            onChange({
                                ...value,
                                type: "css-font",
                                value: value?.value,
                                style: option.value.includes(ItalicFontStyle) ? ItalicFontStyle : RegularFontStyle,
                                variant: option.value,
                                kind: value?.kind,
                                url: foundStyle ? foundStyle.url || value?.url : value?.url,
                                weight: value.weight
                            });
                        }
                    })
                ]
            });
        },
        didSelect: (item)=>{
            const variant = item.state?.variants[0];
            if (variant && item.state) {
                const { weight, style } = getStyleFromVariant(variant);
                const resourceReference = item.state.resources ? item.state.resources[variant] : undefined;
                onChange({
                    ...value,
                    type: "css-font",
                    value: item.state.family,
                    style,
                    variant,
                    kind: item.state.kind,
                    url: item.state.files[variant] || undefined,
                    resourceReference,
                    weight
                });
            }
        },
        render: (item)=>{
            return /*#__PURE__*/ _jsx(PickerRow, {
                item: item
            });
        },
        didClose: ()=>{}
    });
};
/**
 * Renders a row in the picker with font information.
 *
 * @param item - The font item to render.
 */ const PickerRow = ({ item })=>{
    const path = item.state?.family ? item.state?.family.replace(/[^a-zA-Z0-9]/g, "").replace(" ", "").toLowerCase() : undefined;
    const imageUrl = path && item.state?.kind == "google" ? `https://font-preview.superwalleditor.com/preview/${path}.png` : undefined;
    const [imageError, setImageError] = useState(false);
    return /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            /*#__PURE__*/ _jsxs("div", {
                className: "",
                children: [
                    imageUrl && !imageError ? /*#__PURE__*/ _jsx("img", {
                        onError: ()=>setImageError(true),
                        className: "h-[18px] max-w-[100%] my-2 user-select-none w-auto",
                        src: imageUrl
                    }) : /*#__PURE__*/ _jsxs("div", {
                        children: [
                            /*#__PURE__*/ _jsx("p", {
                                style: {
                                    ...item.state?.kind === "custom" ? {} : {}
                                },
                                className: "text-md",
                                children: item.name
                            }),
                            /*#__PURE__*/ _jsx("p", {
                                className: "hidden",
                                children: imageUrl
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsxs("div", {
                        className: "text-sm opacity-50 whitespace-wrap break-all",
                        children: [
                            imageUrl && !imageError || item.state?.kind === "system" ? undefined : "",
                            getLabelFromVariants(item.state?.variants || [])
                        ]
                    })
                ]
            }),
            /*#__PURE__*/ _jsx("div", {
                className: "",
                children: item.next && item.next.length > 0 ? /*#__PURE__*/ _jsx(SIcon, {
                    className: "text-white/50",
                    size: 8,
                    type: "chevron-right-light"
                }) : /*#__PURE__*/ _jsx(STooltip, {
                    content: /*#__PURE__*/ _jsx("div", {
                        className: "text-brand-primary text-sm",
                        children: item.value
                    }),
                    placement: "top",
                    children: /*#__PURE__*/ _jsx("div", {
                        className: "text-brand-primary text-sm truncate max-w-[150px]",
                        children: item.value
                    })
                })
            })
        ]
    });
};
